import React, { useState, useEffect, useCallback } from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import Pagination from "../../components/Pagination";
import { FaArrowLeft, FaStar, FaMapMarkerAlt, FaExternalLinkAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "../../services/axiosConfig";

const InfluencerWelcomePage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrev: false
  });
  const itemsPerPage = 20;

  const categories = [
    { key: "all", label: "All" },
    { key: "fashion", label: "Fashion" },
    { key: "travel", label: "Travel" },
    { key: "sports", label: "Sports" },
    { key: "entertainment", label: "Entertainment" },
    { key: "technology", label: "Technology" },
    { key: "vlogger", label: "Vlogger" },
    { key: "musician", label: "Musician" },
    { key: "photography", label: "Photography" },
    { key: "health and fitness", label: "Health And Fitness" },
    { key: "home decor", label: "Home Decor" },
    { key: "beauty and personal care", label: "Beauty And Personal Care" }
  ];

  const fetchInfluencers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: itemsPerPage,
        page: pagination.currentPage
      });
      
      // Only add category filter if not "all"
      if (selectedCategory !== "all") {
        params.append('category', selectedCategory);
      }
      
      const response = await axios.get(`/auth/users/influencers?${params.toString()}`);
      
      if (response.data.success) {
        setInfluencers(response.data.data);
        setPagination({
          currentPage: response.data.pagination.currentPage,
          totalPages: response.data.pagination.totalPages,
          totalCount: response.data.pagination.totalCount,
          hasNext: response.data.pagination.hasNext,
          hasPrev: response.data.pagination.hasPrev
        });
      } else {
        setError('Failed to fetch influencers');
      }
    } catch (error) {
      console.error('Error fetching influencers:', error);
      setError('Failed to fetch influencers');
    } finally {
      setLoading(false);
    }
  },[selectedCategory, pagination.currentPage]);

  useEffect(() => {
    fetchInfluencers();
  }, [fetchInfluencers]);
  

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page when changing category
    setError(null); // Clear any previous errors
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: page }));
      setError(null); // Clear any previous errors
    }
  };

  // const formatReviews = (reviews) => {
  //   if (reviews >= 1000) {
  //     return `${(reviews / 1000).toFixed(1)}k`;
  //   }
  //   return reviews.toString();
  // };

  const InfluencerCard = ({ influencer }) => {
    const handleViewProfile = () => {
      if (influencer.username) {
        navigate(`/u/${influencer.username}`);
      }
    };

    return (
      <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden cursor-pointer group">
        <div className="relative">
          <div className="w-full h-48 bg-gradient-to-br from-orange-400 to-pink-400 flex items-center justify-center">
            { influencer.instagramProfilePicture || influencer.profileImage?.url ? (
              <img
                src={influencer.instagramProfilePicture || influencer.profileImage.url}
                alt={influencer.username}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-white text-4xl font-bold">
                {influencer.username?.charAt(0).toUpperCase() || '?'}
              </div>
            )}
          </div>
        </div>
        
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-gray-800 text-lg truncate">@{influencer.username}</h3>
            <div className="flex items-center gap-1">
              <FaStar className="text-yellow-400 text-sm" />
              <span className="text-sm font-semibold text-gray-700">4.8</span>
            </div>
          </div>
          
          <div className="flex items-center gap-1 text-gray-500 text-sm mb-3">
            <FaMapMarkerAlt className="text-gray-400" />
            <span>{influencer.country || 'Location not specified'}</span>
          </div>

          {influencer.contentType && influencer.contentType.length > 0 && (
            <div className="mb-3">
              <div className="flex flex-wrap gap-1">
                {influencer.contentType.slice(0, 3).map((category, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-full"
                  >
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </span>
                ))}
                {influencer.contentType.length > 3 && (
                  <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                    +{influencer.contentType.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {/* <div className="font-medium">@{influencer.username}</div> */}
              <div className="text-xs text-gray-500">
                {influencer.gender ? influencer.gender.charAt(0).toUpperCase() + influencer.gender.slice(1) : 'N/A'}
              </div>
            </div>
            <button 
              className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors text-sm font-semibold flex items-center gap-2 group-hover:bg-orange-700"
              onClick={handleViewProfile}
            >
              View Profile
              <FaExternalLinkAlt className="text-xs" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col">
      <Navbar />

      <div className="flex-grow container mx-auto p-6">
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 p-2 rounded-full hover:bg-gray-200 transition-colors"
          >
            <FaArrowLeft size={22} />
          </button>
          <h2 className="text-2xl font-bold text-gray-800">
            {selectedCategory === 'all' 
              ? 'Hire top influencers across all platforms' 
              : `Hire top ${categories.find(cat => cat.key === selectedCategory)?.label} influencers`
            }
          </h2>
        </div>

        {/* Category Tabs */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category.key}
                onClick={() => handleCategoryChange(category.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category.key
                    ? 'bg-orange-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        {/* Category Title */}
        {/* <div className="mb-6">
          <h3 className="text-xl font-bold text-gray-800">
            {categories.find(cat => cat.key === selectedCategory)?.label}
          </h3>
        </div> */}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <div className="text-red-500 text-lg mb-4">{error}</div>
            <button
              onClick={() => {
                setError(null);
                fetchInfluencers();
              }}
              className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Influencers Grid */}
        {!loading && !error && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {influencers.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <div className="text-gray-500 text-lg">No influencers found in this category</div>
                </div>
              ) : (
                influencers.map((influencer) => (
                  <InfluencerCard key={influencer._id} influencer={influencer} />
                ))
              )}
            </div>

            {/* Pagination */}
            {influencers.length > 0 && (
              <div className="mt-12">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  onPageChange={handlePageChange}
                  hasNext={pagination.hasNext}
                  hasPrev={pagination.hasPrev}
                  totalCount={pagination.totalCount}
                  itemsPerPage={itemsPerPage}
                  loading={loading}
                />
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default InfluencerWelcomePage;
